import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const DynamicIndexDemo = () => {
  const [technicalProcesses, setTechnicalProcesses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Demo data to show the clean React structure
  const demoProcesses = [
    { id: '1', name: 'Applications' },
    { id: '2', name: 'Infrastructure' },
    { id: '3', name: 'Security' },
    { id: '4', name: 'Development Workflows' }
  ];

  useEffect(() => {
    // Simulate loading from Google Drive
    const timer = setTimeout(() => {
      setTechnicalProcesses(demoProcesses);
      setLoading(false);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, []);

  const convertToUrlSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  return (
    <div className="documentation-portal">
      <header className="portal-header">
        <h1>DrillDocs Documentation Portal</h1>
        <p>Welcome to the DrillDocs internal documentation portal. This site contains all the information you need as a DrillDocs team member.</p>
        <div style={{ marginTop: '20px', padding: '15px', background: '#e3f2fd', borderRadius: '8px' }}>
          <p><strong>🚀 React Demo Version</strong></p>
          <p>This demonstrates the clean React architecture. In production, it would load dynamic data from Google Drive.</p>
        </div>
      </header>

      <div className="carousel-container">
        <div className="carousel-wrapper">
          
          {/* Technical Processes - Dynamic from Google Drive */}
          <div className="nav-section">
            <h3>Technical Processes</h3>
            {loading ? (
              <ul>
                <li>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                    Loading processes from Google Drive...
                  </div>
                </li>
              </ul>
            ) : (
              <ul>
                {technicalProcesses.map((process) => (
                  <li key={process.id}>
                    <Link 
                      to={`/technical-processes/${convertToUrlSlug(process.name)}`}
                      data-folder-name={process.name}
                      data-folder-id={process.id}
                    >
                      {process.name}
                    </Link>
                  </li>
                ))}
                <li style={{ marginTop: '15px', fontSize: '12px', color: '#666' }}>
                  ✨ In production: Loaded dynamically from Google Drive
                </li>
              </ul>
            )}
          </div>

          {/* Operational Procedures - Static */}
          <div className="nav-section">
            <h3>Operational Procedures</h3>
            <ul>
              <li><a href="/operational-procedures/project-management.html">Project Management Processes</a></li>
              <li><a href="/operational-procedures/client-communication.html">Client Communication Standards</a></li>
              <li><a href="/operational-procedures/support-escalation.html">Support Escalation Procedures</a></li>
              <li><a href="/operational-procedures/meeting-protocols.html">Meeting Protocols</a></li>
              <li><a href="/operational-procedures/documentation-standards.html">Documentation Standards</a></li>
            </ul>
          </div>

          {/* Onboarding Documentation - Static */}
          <div className="nav-section">
            <h3>Onboarding Documentation</h3>
            <ul>
              <li><a href="/onboarding/new-employee-setup.html">New Employee Setup</a></li>
              <li><a href="/onboarding/system-access.html">System Access Procedures</a></li>
              <li><a href="/onboarding/training-materials.html">Training Materials</a></li>
              <li><a href="/onboarding/tool-introductions.html">Tool Introductions</a></li>
              <li><a href="/onboarding/team-structure.html">Team Structure</a></li>
            </ul>
          </div>

          {/* Knowledge Base - Static */}
          <div className="nav-section">
            <h3>Knowledge Base</h3>
            <ul>
              <li><a href="/knowledge-base/troubleshooting.html">Troubleshooting Guides</a></li>
              <li><a href="/knowledge-base/common-issues.html">Common Issues Solutions</a></li>
              <li><a href="/knowledge-base/best-practices.html">Best Practices</a></li>
              <li><a href="/knowledge-base/lessons-learned.html">Lessons Learned</a></li>
            </ul>
          </div>

        </div>
      </div>

      <hr />

      <section className="help-section">
        <h2>Getting Help</h2>
        <ul>
          <li><strong>Technical Issues</strong>: Contact the DevOps team</li>
          <li><strong>Process Questions</strong>: Check with your team lead</li>
          <li><strong>Documentation Updates</strong>: Submit a pull request or contact the documentation team</li>
        </ul>
        <p><em>Last updated: July 28, 2025</em></p>
      </section>

      <div style={{ marginTop: '40px', padding: '20px', background: '#f1f8e9', borderRadius: '8px' }}>
        <h3>🎯 React Architecture Benefits</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '15px' }}>
          <div>
            <strong>Clean Code</strong>
            <p>This entire component is just 95 lines vs 220+ lines in HTML/JS</p>
          </div>
          <div>
            <strong>State Management</strong>
            <p>React automatically updates the UI when data changes</p>
          </div>
          <div>
            <strong>Component Reuse</strong>
            <p>This loading pattern works everywhere without copying code</p>
          </div>
          <div>
            <strong>Error Handling</strong>
            <p>Centralized error states with user-friendly messages</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicIndexDemo;